import {
  Button,
  Center,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  Badge,
  VStack,
  Text,
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spacer,
  Divider,
  Avatar,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useBreakpointValue,
} from '@chakra-ui/react'
import { ChevronLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import {
  useMarkAllNotificationsRead,
  useNotificationCount,
  useNotificationsInfinite,
  useDrawerNotificationsCacheOps,
} from '../../../hooks/useNotifications'
import { patchNotification } from '../../../api/react/notifications/notifications'
import { NotificationBell, Archive, Search, ArrowUpRight, ArrowLeft, BellOutline } from '../../../utils/constants/icons'
import { openNotificationNavigate } from '../../../utils/functions/notificationNavigate'
import debounce from 'lodash/debounce'

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)

const timeAgo = (inputDate: Date): string => {
  const now = new Date()
  const secondsAgo = Math.floor((now.getTime() - inputDate.getTime()) / 1000)

  const units = [
    { name: 'yr', seconds: 31536000 },
    { name: 'mo', seconds: 2592000 },
    { name: 'w', seconds: 604800 },
    { name: 'd', seconds: 86400 },
    { name: 'h', seconds: 3600 },
    { name: 'm', seconds: 60 },
    { name: 's', seconds: 1 },
  ]

  for (const unit of units) {
    const interval = Math.floor(secondsAgo / unit.seconds)
    if (interval >= 1) {
      return `${interval}${unit.name} ago`
    }
  }

  return 'just now'
}

const NotificationRelatedObjectTag = ({ relatedObject } : { relatedObject? : {name : string, logo : string}}) => {
  if (!relatedObject) {
    return <></>
  }

  return <Flex direction="row" alignItems="center" gap="1px">
    <Avatar size="2xs" src={relatedObject.logo} name={relatedObject.name}/>
    <Text fontSize="12" noOfLines={1} maxWidth='150px'>
      {relatedObject.name}
    </Text>
  </Flex>
}

const NotificationCard = ({ notification, onRead, isArchived, isPreview, onDrawerClose }: any) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const isUnread = !notification.read
  const isClickable = !isPreview
  const shouldExpandText = isExpanded

  const isTextTruncated = notification.body && notification.body.length > 80

  const getBoxShadow = () => {
    if (isArchived) return '0 2px 8px -2px rgba(0, 0, 0, 0.08)'

    if (isUnread) {
      return isHovered ? '0 12px 35px -8px rgba(14, 4, 157, 0.2)' : '0 4px 15px -4px rgba(14, 4, 157, 0.05)'
    }

    if (isHovered && isClickable) {
      return '0 6px 20px -4px rgba(0, 0, 0, 0.1)'
    }

    return '0 2px 8px -2px rgba(0, 0, 0, 0.0)'
  }

  const handleClick = () => {
    if (!isClickable) return

    if (!notification.read) {
      onRead(notification)
    }
    openNotificationNavigate(notification.navigate)
    if (onDrawerClose) {
      onDrawerClose()
    }
  }

  return (
    <MotionBox
      p="6px 16px"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.9 }}
      transition={{
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <MotionBox
        bg={isArchived ? 'white' : isUnread ? 'white' : 'neutral.20'}
        borderRadius="12px"
        border="1px solid"
        borderColor={isArchived ? 'neutral.150' : isUnread ? 'neutral.150' : 'neutral.100'}
        p="8px 12px"
        cursor={isClickable ? 'pointer' : 'default'}
        onClick={isClickable ? handleClick : undefined}
        position="relative"
        overflow="hidden"
        animate={{
          scale: !isArchived && isHovered && isUnread ? 1.02 : 1,
          y: !isArchived && isHovered && isUnread ? -2 : 0,
          boxShadow: getBoxShadow(),
          borderColor: !isArchived && isHovered && isUnread ? '#9f7aea' : '#e2e8f0',
        }}
        transition={{
          duration: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        {!isArchived && isUnread && (
          <MotionBox
            position="absolute"
            left="0"
            top="0"
            bottom="0"
            bg="primary.400"
            borderRadius="0 6px 6px 0"
            w="4px"
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{
              scaleY: 1,
              opacity: 1,
              boxShadow: isHovered ? '0 0 12px rgba(14, 4, 157, 0.2)' : '0 0 0px rgba(14, 4, 157, 0)',
            }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        )}

        {!isArchived && isUnread && (
          <MotionBox
            position="absolute"
            left="0"
            top="0"
            right="0"
            bottom="0"
            bg="linear-gradient(135deg, rgba(14, 4, 157, 0.02) 0%, rgba(14, 4, 157, 0.005) 50%, transparent 100%)"
            borderRadius="12px"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        )}

        <MotionBox
          h={shouldExpandText ? 'auto' : '60px'}
          minH={isTextTruncated ? '80px' : '60px'}
          w="100%"
          position="relative"
          zIndex={1}
          animate={{
            height: shouldExpandText ? 'auto' : '50px',
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <VStack align="start" gap="0" flex="1" h="100%">
            <MotionFlex
              mx={1}
              mt={1}
              justify="space-between"
              align="center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              gap="5px"
            >
              <MotionBox
                initial={{ opacity: 0.7 }}
                animate={{
                  opacity: 1,
                  scale: !isArchived && isHovered && isUnread ? 1.05 : 1,
                  y: !isArchived && isHovered && isUnread ? -1 : 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <Text
                  textStyle="b-xs"
                  fontWeight="500"
                  color={isArchived ? 'neutral.500' : isUnread ? 'primary.500' : 'neutral.400'}
                  letterSpacing="1"
                  lineHeight={1}
                >
                  {timeAgo(new Date(notification.created_at))}
                </Text>


              </MotionBox>
              <NotificationRelatedObjectTag relatedObject={notification.member} />
              <NotificationRelatedObjectTag relatedObject={notification.brand} />
            </MotionFlex>

            <MotionBox
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: 1,
                y: 0,
                x: !isArchived && isHovered && isUnread ? 2 : 0,
              }}
              transition={{ duration: 0.4, delay: 0.15 }}
              my={2}
              mx={1}
              flex="1"
            >
              <Text
                textStyle="sm"
                fontSize="14px"
                fontWeight={isArchived ? '400' : isUnread ? '600' : '400'}
                color={isArchived ? 'neutral.600' : isUnread ? 'neutral.900' : 'neutral.600'}
                lineHeight={shouldExpandText ? '1.4' : '1.1'}
                letterSpacing="0.1px"
                noOfLines={shouldExpandText ? undefined : 2}
                overflow={shouldExpandText ? 'visible' : 'hidden'}
              >
                {notification.body}
              </Text>

              {isTextTruncated && !shouldExpandText && !isArchived && (
                <Flex justify="flex-end" mt="4px">
                  <Text
                    fontSize="12px"
                    color="neutral.400"
                    cursor="pointer"
                    _hover={{ color: 'neutral.600' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsExpanded(true)
                    }}
                  >
                    show more
                  </Text>
                </Flex>
              )}

              {shouldExpandText && (
                <Flex justify="flex-end" mt="4px">
                  <Text
                    fontSize="12px"
                    color="neutral.400"
                    cursor="pointer"
                    _hover={{ color: 'neutral.600' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsExpanded(false)
                    }}
                  >
                    show less
                  </Text>
                </Flex>
              )}
            </MotionBox>
          </VStack>
        </MotionBox>

        {!isArchived && isUnread && (
          <MotionBox
            position="absolute"
            right="13px"
            top="13px"
            w="8px"
            h="8px"
            bg="primary.400"
            borderRadius="full"
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: isHovered && isUnread ? 1.2 : 1,
              opacity: 1,
              boxShadow: isHovered && isUnread ? '0 0 0 3px rgba(14, 4, 157, 0.2)' : '0 0 0 0px rgba(14, 4, 157, 0)',
            }}
            transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
          />
        )}
      </MotionBox>
    </MotionBox>
  )
}

const NotificationBellButton = ({
  isOpen,
  onOpen,
  onClose,
}: {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}) => {
  const [search, setSearch] = useState('')
  const [showAllUnread, setShowAllUnread] = useState(false)
  const drawerCacheOps = useDrawerNotificationsCacheOps(search)
  const drawerCacheOpsRef = useRef(drawerCacheOps)
  drawerCacheOpsRef.current = drawerCacheOps
  const scrollRef = useRef<HTMLDivElement>(null)
  const readScrollRef = useRef<HTMLDivElement>(null)

  const notificationCount = useNotificationCount({ archived: false, favorite: false, read: false })

  const unreadNotificationsQuery = useNotificationsInfinite(
    { page_size: 50, search, archived: false, favorite: undefined, read: false, expand: 'member,brand' },
    'drawer-unread-notifications',
    isOpen,
  )

  const readNotificationsQuery = useNotificationsInfinite(
    { page_size: 50, search, archived: false, favorite: undefined, read: true },
    'drawer-read-notifications',
    isOpen,
  )

  const pendingChangesRef = useRef<{ [id: string]: any }>({})

  const allUnreadNotifications = unreadNotificationsQuery.data?.pages.flatMap((page) => page.data) ?? []
  const allReadNotifications = readNotificationsQuery.data?.pages.flatMap((page) => page.data) ?? []
  const allNotifications = [...allUnreadNotifications, ...allReadNotifications]

  const debouncedMutate = useRef(
    debounce(() => {
      const changes = { ...pendingChangesRef.current }
      pendingChangesRef.current = {}

      const notificationsToUpdate = Object.keys(changes).map((id) => ({
        id,
        ...changes[id],
      }))

      const promises = notificationsToUpdate.map((notification) => patchNotification(notification))

      Promise.allSettled(promises).then((results) => {
        if (results.some((r) => r.status === 'rejected')) {
          drawerCacheOpsRef.current.invalidateInfinite()
        }
      })
    }, 500)
  ).current

  const handleStarClick = (notif: any) => {
    const wasUnread = !notif.read

    // Update both queries
    drawerCacheOps.setUnread((oldData: any) => {
      if (!oldData) return oldData
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.filter((n: any) => n.id !== notif.id), // Remove from unread if it was unread
      }))
      return { ...oldData, pages: newPages }
    })

    drawerCacheOps.setRead((oldData: any) => {
      if (!oldData) return oldData
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.map((n: any) => (n.id === notif.id ? { ...n, favorite: !notif.favorite, read: true } : n)),
      }))

      // If notification was unread, add it to read notifications
      if (wasUnread) {
        const updatedNotification = { ...notif, favorite: !notif.favorite, read: true }
        if (newPages.length > 0) {
          newPages[0] = {
            ...newPages[0],
            data: [updatedNotification, ...newPages[0].data],
          }
        }
      }

      return { ...oldData, pages: newPages }
    })

    pendingChangesRef.current[notif.id] = {
      ...pendingChangesRef.current[notif.id],
      favorite: !notif.favorite,
      read: true,
    }

    debouncedMutate()
  }

  const handleArchiveClick = (notif: any) => {
    // Remove from both queries
    drawerCacheOps.setUnread((oldData: any) => {
      if (!oldData) return oldData
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.filter((n: any) => n.id !== notif.id),
      }))
      return { ...oldData, pages: newPages }
    })

    drawerCacheOps.setRead((oldData: any) => {
      if (!oldData) return oldData
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.filter((n: any) => n.id !== notif.id),
      }))
      return { ...oldData, pages: newPages }
    })

    pendingChangesRef.current[notif.id] = {
      ...pendingChangesRef.current[notif.id],
      archived: true,
      read: true,
    }

    debouncedMutate()
  }

  const handleReadClick = (notif: any) => {
    // Move from unread to read
    drawerCacheOps.setUnread((oldData: any) => {
      if (!oldData) return oldData
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.filter((n: any) => n.id !== notif.id),
      }))
      return { ...oldData, pages: newPages }
    })

    drawerCacheOps.setRead((oldData: any) => {
      if (!oldData) return oldData
      const updatedNotification = { ...notif, read: true }
      const newPages = [...oldData.pages]

      if (newPages.length > 0) {
        newPages[0] = {
          ...newPages[0],
          data: [updatedNotification, ...newPages[0].data],
        }
      }

      return { ...oldData, pages: newPages }
    })

    pendingChangesRef.current[notif.id] = {
      ...pendingChangesRef.current[notif.id],
      read: true,
    }

    debouncedMutate()
    drawerCacheOps.invalidateCount()
  }

  const markAllAsReadMutation = useMarkAllNotificationsRead()

  const handleMarkAllAsRead = () => {
    if (allUnreadNotifications.length > 0) {
      markAllAsReadMutation.mutate(allUnreadNotifications)
    }
  }

  const unreadCount = notificationCount.data?.count ?? 0
  const unreadNotifications = allUnreadNotifications
  const hasUnreadNotifications = unreadNotifications.length > 0
  const hasReadNotifications = allReadNotifications.length > 0

  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false

  useEffect(() => {
    return () => {
      debouncedMutate.cancel()
    }
  }, [])

  useEffect(() => {
    const handleUnreadScroll = (e: any) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        unreadNotificationsQuery.hasNextPage &&
        !unreadNotificationsQuery.isFetchingNextPage
      ) {
        unreadNotificationsQuery.fetchNextPage()
      }
    }

    const handleReadScroll = (e: any) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        readNotificationsQuery.hasNextPage &&
        !readNotificationsQuery.isFetchingNextPage
      ) {
        readNotificationsQuery.fetchNextPage()
      }
    }

    const scrollElement = scrollRef.current
    const readScrollElement = readScrollRef.current

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleUnreadScroll)
    }
    if (readScrollElement) {
      readScrollElement.addEventListener('scroll', handleReadScroll)
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleUnreadScroll)
      }
      if (readScrollElement) {
        readScrollElement.removeEventListener('scroll', handleReadScroll)
      }
    }
  }, [
    unreadNotificationsQuery.hasNextPage,
    unreadNotificationsQuery.isFetchingNextPage,
    unreadNotificationsQuery.fetchNextPage,
    readNotificationsQuery.hasNextPage,
    readNotificationsQuery.isFetchingNextPage,
    readNotificationsQuery.fetchNextPage,
  ])

  return (
    <>
      <MotionBox
        position="relative"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        <Button
          mr="2"
          bg="neutral.50"
          h="36px"
          w="36px"
          borderRadius="full"
          variant="neutral"
          size="sm"
          onClick={onOpen}
          position="relative"
          _hover={{ bg: 'neutral.100' }}
        >
          <Center cursor="pointer" w="34px" h="34px" borderRadius="full" bg="transparent">
            <Icon viewBox="0 0 24 24" w="16px" h="16px" color="neutral.700">
              <NotificationBell />
            </Icon>
          </Center>
        </Button>

        {unreadCount > 0 && (
          <MotionBox
            position="absolute"
            top="-2px"
            right="-2px"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 15 }}
          >
            <Badge
              colorScheme="primary"
              borderRadius="full"
              fontSize="xs"
              minW="18px"
              h="18px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          </MotionBox>
        )}
      </MotionBox>

      {isMobile && (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="full" autoFocus={false}>
          <DrawerOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
          <DrawerContent
            w="100vw"
            maxW="100vw"
            h="100dvh"
            maxH="100dvh"
            sx={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <DrawerHeader p="8px 8px 8px 8px" m="0" borderBottomWidth="1px" borderBottomColor="neutral.50">
              <Flex align="center" gap="4px" h="44px">
                <IconButton
                  aria-label="Close"
                  icon={<ChevronLeft size={24} strokeWidth={2.25} />}
                  variant="ghost"
                  onClick={onClose}
                  h="44px"
                  w="44px"
                  minW="44px"
                  color="neutral.900"
                  bg="transparent"
                  _hover={{ bg: 'transparent' }}
                  _active={{ bg: 'transparent', transform: 'scale(0.92)' }}
                  transition="transform 120ms ease-out"
                  sx={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
                />
                <Text
                  fontSize="20px"
                  fontWeight="700"
                  color="neutral.900"
                  letterSpacing="-0.02em"
                >
                  Notifications
                </Text>
                {unreadCount > 0 && (
                  <Center
                    ml="6px"
                    minW="22px"
                    h="22px"
                    px="7px"
                    borderRadius="999px"
                    bg="primary.500"
                  >
                    <Text fontSize="11px" fontWeight="700" color="white" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Text>
                  </Center>
                )}
              </Flex>
            </DrawerHeader>

            <Box p="12px 16px" borderBottomWidth="1px" borderBottomColor="neutral.50" flexShrink={0}>
              <Flex gap="8px">
                <InputGroup size="sm" flex="1">
                  <InputLeftElement pointerEvents="none" h="44px">
                    <Icon ml="2" boxSize="18px" fill="transparent" color="neutral.400">
                      <Search />
                    </Icon>
                  </InputLeftElement>
                  <Input
                    placeholder="Search notifications..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    h="44px"
                    fontSize="16px"
                    pl="40px"
                    bg="neutral.20"
                    border="1px solid"
                    borderColor="neutral.100"
                    borderRadius="10px"
                    _focus={{ bg: 'white', borderColor: 'primary.400' }}
                  />
                </InputGroup>
                <Button
                  h="44px"
                  variant="outline"
                  fontSize="13px"
                  fontWeight="600"
                  px="14px"
                  isDisabled={allUnreadNotifications.length === 0}
                  isLoading={markAllAsReadMutation.isPending}
                  onClick={handleMarkAllAsRead}
                  bg="white"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor="neutral.100"
                  _hover={{ bg: 'white', borderColor: 'primary.300' }}
                  _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  Read all
                </Button>
              </Flex>
            </Box>

            <DrawerBody p="0" sx={{ WebkitOverflowScrolling: 'touch' }}>
              {showAllUnread ? (
                <Box>
                  <Box position="sticky" top="0" bg="white" zIndex={1} borderBottomWidth="1px" borderBottomColor="neutral.50">
                    <Flex align="center" gap="8px" h="52px" px="8px">
                      <IconButton
                        aria-label="Back"
                        icon={<ChevronLeft size={22} strokeWidth={2.25} />}
                        variant="ghost"
                        onClick={() => setShowAllUnread(false)}
                        h="44px"
                        w="44px"
                        minW="44px"
                        color="neutral.900"
                        bg="transparent"
                        _hover={{ bg: 'transparent' }}
                        _active={{ bg: 'transparent', transform: 'scale(0.92)' }}
                      />
                      <Text fontSize="15px" fontWeight="700" color="neutral.900">
                        All Read Notifications
                      </Text>
                    </Flex>
                  </Box>

                  {readNotificationsQuery.isLoading ? (
                    <VStack spacing="0" pt="2" align="stretch">
                      {[...Array(8)].map((_, i) => (
                        <Box key={i} p="16px" borderBottomWidth="1px" borderBottomColor="neutral.50">
                          <Flex gap="12px">
                            <Box w="4px" h="48px" bg="neutral.100" borderRadius="2px" />
                            <VStack align="start" spacing="8px" flex="1">
                              <Box w="60px" h="12px" bg="neutral.100" borderRadius="4px" />
                              <Box w="100%" h="16px" bg="neutral.100" borderRadius="4px" />
                              <Box w="40%" h="12px" bg="neutral.100" borderRadius="4px" />
                            </VStack>
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  ) : allNotifications.filter((n: any) => n.read).length === 0 ? (
                    <Flex direction="column" align="center" justify="center" py="80px">
                      <Icon boxSize="48px" color="neutral.300" mb="16px">
                        <NotificationBell />
                      </Icon>
                      <Text color="neutral.500" fontSize="15px" fontWeight="500" textAlign="center">
                        No read notifications
                      </Text>
                    </Flex>
                  ) : (
                    <VStack spacing="0" pt={2} pb={5} align="stretch">
                      {allNotifications
                        .filter((notification: any) => notification.read)
                        .map((notification: any, index: number) => (
                          <NotificationCard
                            key={notification.id}
                            notification={notification}
                            onStar={handleStarClick}
                            onArchive={handleArchiveClick}
                            onRead={handleReadClick}
                            index={index}
                            isArchived={false}
                            isPreview={false}
                            onDrawerClose={onClose}
                          />
                        ))}
                    </VStack>
                  )}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" minH="100%">
                  <Box flex="1">
                  {unreadNotificationsQuery.isLoading ? (
                    <VStack spacing="0" align="stretch">
                      {[...Array(8)].map((_, i) => (
                        <Box key={i} p="16px" borderBottomWidth="1px" borderBottomColor="neutral.50">
                          <Flex gap="12px">
                            <Box w="4px" h="48px" bg="neutral.100" borderRadius="2px" />
                            <VStack align="start" spacing="8px" flex="1">
                              <Box w="60px" h="12px" bg="neutral.100" borderRadius="4px" />
                              <Box w="100%" h="16px" bg="neutral.100" borderRadius="4px" />
                              <Box w="40%" h="12px" bg="neutral.100" borderRadius="4px" />
                            </VStack>
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  ) : !hasUnreadNotifications ? (
                    <Flex direction="column" align="center" justify="center" h="100%" py="80px" px="24px">
                      <Icon boxSize="56px" color="primary.400" mb="20px">
                        <BellOutline />
                      </Icon>
                      <Text color="neutral.900" fontSize="18px" fontWeight="700" mb="8px" letterSpacing="-0.01em">
                        All caught up!
                      </Text>
                      <Text color="neutral.500" fontSize="14px" fontWeight="400" lineHeight="1.4" textAlign="center">
                        You're up to date with all your notifications.
                      </Text>
                      <Text color="success.400" fontSize="11px" fontWeight="700" letterSpacing="1px" mt="12px">
                        INBOX ZERO
                      </Text>
                    </Flex>
                  ) : (
                    <VStack spacing="0" align="stretch" py="2">
                      {allNotifications
                        .filter((notification: any) => !notification.read)
                        .map((notification: any, filteredIndex: number) => (
                          <NotificationCard
                            key={notification.id}
                            notification={notification}
                            onStar={handleStarClick}
                            onArchive={handleArchiveClick}
                            onRead={handleReadClick}
                            index={filteredIndex}
                            isArchived={false}
                            isPreview={false}
                            onDrawerClose={onClose}
                          />
                        ))}
                    </VStack>
                  )}
                  </Box>

                  {hasReadNotifications && (
                    <Box
                      bg="neutral.10"
                      borderTopWidth="1px"
                      borderTopColor="neutral.100"
                      pt="14px"
                      pb="16px"
                      flexShrink={0}
                    >
                      <Flex w="full" align="center" gap="6px" mb="10px" px="16px">
                        <Icon boxSize="16px" fill="transparent" color="neutral.500">
                          <Archive />
                        </Icon>
                        <Text fontSize="13px" fontWeight="700" color="neutral.600" letterSpacing="0.02em">
                          Recently Read
                        </Text>
                        <Spacer />
                        <Flex
                          as="button"
                          align="center"
                          gap="4px"
                          onClick={() => setShowAllUnread(true)}
                          _active={{ opacity: 0.7 }}
                        >
                          <Text fontSize="13px" fontWeight="700" color="primary.500">
                            Show all
                          </Text>
                          <Icon boxSize="14px" color="primary.500">
                            <ArrowUpRight />
                          </Icon>
                        </Flex>
                      </Flex>
                      <VStack spacing="0" align="stretch">
                        {allNotifications
                          .filter((n: any) => n.read)
                          .slice(0, 2)
                          .map((notification: any, idx: number) => (
                            <NotificationCard
                              key={`recent-${notification.id}`}
                              notification={notification}
                              onStar={handleStarClick}
                              onArchive={handleArchiveClick}
                              onRead={handleReadClick}
                              index={idx}
                              isArchived={true}
                              isPreview={false}
                              onDrawerClose={onClose}
                            />
                          ))}
                      </VStack>
                    </Box>
                  )}
                </Box>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Modal
        isOpen={!isMobile && isOpen}
        onClose={onClose}
        motionPreset="slideInRight"
        isCentered={false}
        scrollBehavior="inside"
        blockScrollOnMount={false}
        preserveScrollBarGap
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
        <ModalContent
          position="fixed"
          right="0"
          w="493px"
          maxW="calc(100vw - 20px)"
          height="100vh"
          maxH="100vh"
          m="0"
          p="0"
          borderRadius="12px"
          overflow="hidden"
        >
          <MotionBox
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            h="100%"
            display="flex"
            flexDirection="column"
            minH="0"
          >
            <ModalHeader p="12px 16px" m="0" flexShrink={0}>
              <MotionBox
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <Text fontWeight="590" fontSize="xl" textStyle="b-md" mt="1">
                  Notifications
                </Text>
              </MotionBox>
            </ModalHeader>

            <ModalCloseButton />

            <Divider my="1" flexShrink={0} />

            <Box p="12px 16px" flexShrink={0}>
              <VStack align="stretch" spacing="12px">
                <MotionFlex
                  gap="8px"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <InputGroup size="sm" flex="1">
                    <InputLeftElement pointerEvents="none">
                      <Icon ml="2" mt="6px" boxSize="22px" fill="transparent" color="neutral.400">
                        <Search />
                      </Icon>
                    </InputLeftElement>
                    <Input
                      placeholder="Search notifications..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      fontSize="12px"
                      bg="neutral.20"
                      border="1px solid"
                      borderColor="neutral.100"
                      _focus={{ bg: 'white', borderColor: 'primary.400' }}
                    />
                  </InputGroup>

                  <Button
                    size="sm"
                    variant="outline"
                    fontSize="12px"
                    px="12px"
                    isDisabled={allUnreadNotifications.length === 0}
                    isLoading={markAllAsReadMutation.isPending}
                    onClick={handleMarkAllAsRead}
                    colorScheme={allUnreadNotifications.length > 0 ? 'primary' : 'gray'}
                    bg="white"
                    _hover={{ bg: 'white', borderColor: 'primary.300', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}
                    border="1px solid"
                    borderColor="neutral.100"
                    _disabled={{
                      opacity: 0.4,
                      cursor: 'not-allowed',
                    }}
                  >
                    Read all
                  </Button>
                </MotionFlex>
              </VStack>
            </Box>

            <Divider mt="1" flexShrink={0} />

            <ModalBody p="0" overflow="hidden" flex="1" minH="0">
              {showAllUnread ? (
                <MotionBox
                  w="100%"
                  h="100%"
                  position="relative"
                  overflow="hidden"
                  initial={{ y: 250 }}
                  animate={{ y: 0 }}
                  exit={{ y: 250 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <MotionBox
                    h="52px"
                    pb={2}
                    borderBottom="1px solid"
                    borderBottomColor="neutral.10"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <Flex align="center" gap="12px" mt="2">
                      <IconButton
                        ml={4}
                        pl={2}
                        pt={2}
                        aria-label="Back"
                        icon={
                          <Icon boxSize="28px" color="neutral.700">
                            <ArrowLeft />
                          </Icon>
                        }
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllUnread(false)}
                        _hover={{ bg: 'neutral.50' }}
                      />
                      <Text textStyle="b-sm" fontWeight="600" color="neutral.900">
                        All Read Notifications
                      </Text>
                    </Flex>
                  </MotionBox>

                  <Box
                    ref={readScrollRef}
                    h="calc(100% - 52px)"
                    overflowY="auto"
                    overflowX="hidden"
                  >
                    <AnimatePresence mode="wait">
                      {readNotificationsQuery.isLoading ? (
                        <VStack spacing="0" pt="2" align="stretch">
                          {[...Array(8)].map((_, i) => (
                            <MotionBox
                              key={i}
                              p="16px 24px"
                              borderBottomWidth="1px"
                              borderBottomColor="neutral.50"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                            >
                              <Flex gap="12px">
                                <Box w="4px" h="48px" bg="neutral.100" borderRadius="2px" />
                                <VStack align="start" spacing="8px" flex="1">
                                  <Box w="60px" h="12px" bg="neutral.100" borderRadius="4px" />
                                  <Box w="100%" h="16px" bg="neutral.100" borderRadius="4px" />
                                  <Box w="40%" h="12px" bg="neutral.100" borderRadius="4px" />
                                </VStack>
                              </Flex>
                            </MotionBox>
                          ))}
                        </VStack>
                      ) : allNotifications.filter((n: any) => n.read).length === 0 ? (
                        <MotionFlex
                          direction="column"
                          align="center"
                          justify="center"
                          py="80px"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Icon boxSize="48px" color="neutral.300" mb="16px">
                            <NotificationBell />
                          </Icon>
                          <Text color="neutral.500" textStyle="b-md" fontWeight="400" textAlign="center">
                            No read notifications
                          </Text>
                        </MotionFlex>
                      ) : (
                        <VStack spacing="0" pt={2} pb={5} align="stretch">
                          {(() => {
                            const readNotifications = allNotifications.filter((notification: any) => notification.read)
                            return readNotifications
                          })().map((notification: any, index: number) => (
                            <MotionBox
                              key={notification.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05, duration: 0.3 }}
                            >
                              <NotificationCard
                                notification={notification}
                                onStar={handleStarClick}
                                onArchive={handleArchiveClick}
                                onRead={handleReadClick}
                                index={index}
                                isArchived={false}
                                isPreview={false}
                                onDrawerClose={onClose}
                              />
                            </MotionBox>
                          ))}
                        </VStack>
                      )}

                      {readNotificationsQuery.isFetchingNextPage && !readNotificationsQuery.isFetching && (
                        <Center py="20px">
                          <Text color="neutral.500" textStyle="b-sm">
                            Loading more notifications...
                          </Text>
                        </Center>
                      )}
                    </AnimatePresence>
                  </Box>
                </MotionBox>
              ) : (
                <Box w="100%" h="calc(100vh - 131px)" position="relative" overflowX="hidden" overflowY="hidden">
                  <Box
                    ref={scrollRef}
                    h={hasReadNotifications ? 'calc(100% - 180px)' : '100%'}
                    overflowY="auto"
                    overflowX="hidden"
                  >
                    <AnimatePresence mode="wait">
                      {unreadNotificationsQuery.isLoading ? (
                        <VStack spacing="0" align="stretch">
                          {[...Array(8)].map((_, i) => (
                            <MotionBox
                              key={i}
                              p="16px 24px"
                              borderBottomWidth="1px"
                              borderBottomColor="neutral.50"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.1, duration: 0.3 }}
                            >
                              <Flex gap="12px">
                                <Box w="4px" h="48px" bg="neutral.100" borderRadius="2px" />
                                <VStack align="start" spacing="8px" flex="1">
                                  <Box w="60px" h="12px" bg="neutral.100" borderRadius="4px" />
                                  <Box w="100%" h="16px" bg="neutral.100" borderRadius="4px" />
                                  <Box w="40%" h="12px" bg="neutral.100" borderRadius="4px" />
                                </VStack>
                              </Flex>
                            </MotionBox>
                          ))}
                        </VStack>
                      ) : allNotifications.length === 0 ||
                        allNotifications.filter((notification: any) => !notification.read).length === 0 ? (
                        <MotionFlex
                          direction="column"
                          align="center"
                          justify="center"
                          py="80px"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, type: 'spring' }}
                        >
                          <MotionBox
                            mb="24px"
                            animate={{
                              scale: [1, 1.1, 1],
                              rotate: [0, 5, 0, -5, 0],
                            }}
                            transition={{
                              scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
                              rotate: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
                            }}
                          >
                            <Icon boxSize="56px" color="primary.400">
                              <BellOutline />
                            </Icon>
                          </MotionBox>

                          <MotionBox
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                            textAlign="center"
                          >
                            <Text color="neutral.900" textStyle="b-lg" fontWeight="600" mb="8px">
                              All caught up!
                            </Text>
                            <Text color="neutral.500" textStyle="b-sm" fontWeight="400" lineHeight="1.4">
                              You're up to date with all your notifications.
                            </Text>
                          </MotionBox>
                          <MotionBox
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                            textAlign="center"
                          >
                            <Text color="success.400" textStyle="b-xs" fontWeight="600" letterSpacing="1px" mt="8px">
                              INBOX ZERO
                            </Text>
                          </MotionBox>
                        </MotionFlex>
                      ) : (
                        <VStack spacing="0" align="stretch" py="2">
                          {allNotifications
                            .filter((notification: any) => !notification.read)
                            .map((notification: any, filteredIndex: number) => {
                              const originalIndex = allNotifications.findIndex((n: any) => n.id === notification.id)
                              return (
                                <MotionBox
                                  key={notification.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: filteredIndex * 0.05, duration: 0.3 }}
                                >
                                  <NotificationCard
                                    notification={notification}
                                    onStar={handleStarClick}
                                    onArchive={handleArchiveClick}
                                    onRead={handleReadClick}
                                    index={originalIndex}
                                    isArchived={false}
                                    isPreview={false}
                                    onDrawerClose={onClose}
                                  />
                                </MotionBox>
                              )
                            })}
                        </VStack>
                      )}

                      {/* Loading indicator for infinite scroll */}
                      {unreadNotificationsQuery.isFetchingNextPage && (
                        <Center py="20px">
                          <Text color="neutral.500" textStyle="b-sm">
                            Loading more notifications...
                          </Text>
                        </Center>
                      )}
                    </AnimatePresence>
                  </Box>

                  {hasReadNotifications && (
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      h="180px"
                      bg="linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(250,248,255,0.6) 15%, rgba(247,244,255,0.8) 30%, rgba(248,246,254,0.95) 60%, rgba(249,248,255,1) 100%)"
                      borderTop="1px solid"
                      borderTopColor="neutral.100"
                      py="16px"
                      borderBottomRadius="12px"
                    >
                      <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      >
                        <Flex w="full" align="center" gap="6px" mb="12px" px="16px">
                          <Icon boxSize="18px" fill="transparent" color="neutral.500">
                            <Archive />
                          </Icon>
                          <Text textStyle="b-sm" mb="1" fontWeight="600" color="neutral.600">
                            Recently Read
                          </Text>
                          <Spacer />
                          <Flex
                            align="center"
                            gap="4px"
                            cursor="pointer"
                            _hover={{ opacity: 0.8 }}
                            transition="opacity 0.2s"
                            onClick={() => setShowAllUnread(true)}
                          >
                            <Text textStyle="b-sm" mb="1" fontWeight="600" color="primary.400">
                              Show all
                            </Text>
                            <Icon boxSize="16px" color="primary.400" mb="1">
                              <ArrowUpRight />
                            </Icon>
                          </Flex>
                        </Flex>

                        <Box position="relative" h="130px">
                          {allNotifications
                            .filter((notification: any) => notification.read)
                            .reverse()
                            .slice(-2)
                            .reverse()
                            .map((notification: any, stackIndex: number) => {
                              const originalIndex = allNotifications.findIndex((n: any) => n.id === notification.id)
                              return (
                                <MotionBox
                                  key={`archive-${notification.id}`}
                                  position="absolute"
                                  w="calc(100% - 8px)"
                                  pl="2"
                                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                                  animate={{
                                    opacity: 1 - stackIndex * 0.15,
                                    y: stackIndex * 18,
                                    scale: 1 - stackIndex * 0.03,
                                    x: stackIndex * 6,
                                  }}
                                  transition={{
                                    duration: 0.5,
                                    delay: stackIndex * 0.1,
                                    ease: [0.25, 0.46, 0.45, 0.94],
                                  }}
                                  style={{ zIndex: 3 - stackIndex }}
                                >
                                  <Box transform={`scale(${1 - stackIndex * 0.04})`} transformOrigin="top left">
                                    <NotificationCard
                                      notification={notification}
                                      onStar={handleStarClick}
                                      onArchive={handleArchiveClick}
                                      onRead={handleReadClick}
                                      index={originalIndex}
                                      isArchived={true}
                                      isPreview={true}
                                    />
                                  </Box>
                                </MotionBox>
                              )
                            })}
                        </Box>
                      </MotionBox>
                    </Box>
                  )}
                </Box>
              )}
            </ModalBody>
          </MotionBox>
        </ModalContent>
      </Modal>
    </>
  )
}

export default NotificationBellButton
